import { BackIcon } from '@euler/app/components/icons/BackIcon';
import {
  StatusDotLabel,
  StatusTone,
} from '@euler/app/flows/erp/components/StatusDotLabel';
import { useErpProviders } from '@euler/app/flows/erp/functions/useErpProviders';
import { showErpToast } from '@euler/app/flows/erp/utils/showErpToast';
import { Button } from '@euler/components/Button';
import { Colors, StatusColors } from '@euler/components/Colors';
import { TableView } from '@euler/components/TableView';
import { FontFamily } from '@euler/components/typography';
import { useStoreFeature } from '@euler/functions/entitlements/useStoreFeature';
import { useConfirm } from '@euler/functions/ux/useConfirm';
import { wrapNavigatorScreen } from '@euler/functions/wrapNavigatorScreen';
import {
  ErpIntegrationType,
  ErpLoginStatus,
  StoreFeature,
} from '@euler/model/enum';
import {
  ErpConnectionInfo,
  ErpIntegrationInfo,
} from '@euler/model/erp-integrations';
import { useServiceFactory } from '@euler/services/factory';
import { usePersistFn } from '@euler/utils/hooks';
import { HeaderBackButton } from '@react-navigation/elements';
import { useFocusEffect, useNavigation } from '@react-navigation/native';
import dayjs from 'dayjs';
import { memo, useCallback, useState } from 'react';
import { ActivityIndicator, ScrollView, Text, View } from 'react-native';
import {
  Cell2 as Cell,
  Section,
} from '../../settings/components/SettingsTable';

export const ErpManagementScreen = wrapNavigatorScreen(
  () => {
    return <Content />;
  },
  // This is the ERP stack's first route, so that stack has nothing to pop and
  // hides the default back button. The flow is hosted by the settings stack
  // (`/settings` sits beneath it even on a cold deep link), so `goBack`
  // bubbles up to it and returns to the settings list.
  ({ navigation }) => ({
    title: '服务集成与管理',
    headerLeft: props =>
      navigation.canGoBack() ? (
        <HeaderBackButton
          {...props}
          backImage={() => <BackIcon fill="#fff" />}
          onPress={() => navigation.goBack()}
        />
      ) : null,
  }),
);

const formatTime = (value?: string | Date | null) =>
  value ? dayjs(value).format('YYYY-MM-DD HH:mm') : '--';

const Content = memo(() => {
  const navigation = useNavigation<any>();
  const confirm = useConfirm();
  const { storeErpIntegrationService } = useServiceFactory();
  const { getProviderName, getProviderTitle } = useErpProviders();

  // The entitlement covers the extension and hosted mechanisms. Without it a
  // store can still reach this screen — an openapi integration is provisioned
  // by us and shows here — but cannot set one up itself, and the server
  // refuses those calls regardless of what the screen offers.
  const canIntegrate = useStoreFeature(StoreFeature.ErpIntegration).enabled;
  const [loading, setLoading] = useState(true);
  const [connection, setConnection] = useState<ErpConnectionInfo | undefined>();

  // Only one connection can be in use at a time, and it is the one to show.
  // A store can also carry dormant connections — rows left behind once every
  // integration on them was removed — which must not be mistaken for the
  // live one, so pick by activity rather than by position.
  const load = useCallback(async () => {
    try {
      const { connections } =
        await storeErpIntegrationService.getErpConnections();
      setConnection(connections.find(x => x.activeIntegrationCount > 0));
    } catch (e: any) {
      showErpToast('加载失败：' + (e.message || '未知错误'), 'error');
    } finally {
      setLoading(false);
    }
  }, [storeErpIntegrationService]);

  useFocusEffect(
    useCallback(() => {
      void load();
    }, [load]),
  );

  const integrations = connection?.activeIntegrations ?? [];
  const extension = integrations.find(
    x => x.type === ErpIntegrationType.Extension,
  );
  const hosted = integrations.find(x => x.type === ErpIntegrationType.Hosted);
  const providerName = getProviderName(connection?.erpProviderId, '当前服务商');
  const providerTitle = getProviderTitle(
    connection?.erpProviderId,
    '当前服务商',
  );

  const onStartIntegration = usePersistFn(() => {
    navigation.replace('_erpProviderSelection');
  });

  // The connection already names the provider, so setting up the hosted
  // integration skips provider selection entirely.
  const onSetupHosted = usePersistFn(() => {
    if (!connection) return;
    navigation.navigate('_erpLogin', {
      providerId: connection.erpProviderId,
      providerName,
    });
  });

  const onDeactivate = usePersistFn(
    async (integration: ErpIntegrationInfo, label: string) => {
      const confirmed = await confirm({
        title: `停用${label}`,
        message: `确定要停用${providerName}的${label}吗？停用后相关数据同步将停止。`,
        confirmButtonText: '停用',
        cancelButtonText: '取消',
        destructive: true,
      });
      if (!confirmed) return;

      try {
        await storeErpIntegrationService.deactivateErpIntegration(
          integration.id,
        );
        showErpToast('已停用', 'success');
        void load();
      } catch (e: any) {
        showErpToast('停用失败：' + (e.message || '未知错误'), 'error');
      }
    },
  );

  const onRemoveHosted = usePersistFn(async () => {
    if (!hosted) return;
    const confirmed = await confirm({
      title: '解除云端托管集成',
      message:
        `确定要解除${providerName}的云端托管集成吗？` +
        '解除后将停止订单同步，需要重新登录才能恢复。',
      confirmButtonText: '解除集成',
      cancelButtonText: '取消',
      destructive: true,
    });
    if (!confirmed) return;

    try {
      await storeErpIntegrationService.removeErpIntegration(hosted.id);
      showErpToast('已解除集成', 'success');
      void load();
    } catch (e: any) {
      showErpToast('解除失败：' + (e.message || '未知错误'), 'error');
    }
  });

  if (loading) {
    return (
      <View css="flex: 1; align-items: center; justify-content: center;">
        <ActivityIndicator size="large" color={Colors.Gray2} />
      </View>
    );
  }

  // State 1: nothing is live. A dormant connection may still exist, which is
  // why the CTA goes to provider selection rather than straight to login.
  if (!connection || integrations.length === 0) {
    return (
      <EmptyState onPress={canIntegrate ? onStartIntegration : undefined} />
    );
  }

  return (
    <ScrollView
      css="flex: 1; background-color: #f7f7f7;"
      contentContainerStyle={{ paddingBottom: 24 }}
    >
      <TableView>
        <Section title="服务商连接">
          <Cell title="服务商" detail={providerTitle} />
          {/* Only shown without a hosted integration: with one, 常用操作
              carries the same state on a row that can actually be opened,
              and the mapping screen needs a hosted session to load the ERP's
              staff anyway. */}
          {!hosted && (
            <Cell
              title="员工绑定"
              detail={<StatusDotLabel {...mappingStatus(connection)} />}
            />
          )}
        </Section>

        {/* Always shown, set up or not: the plugin is configured on a PC, so
            a store that has not installed it needs to be told that here
            rather than left wondering whether the app is missing it. */}
        <Section
          title="浏览器插件集成"
          tail={
            extension
              ? '浏览器插件集成由电脑端插件维护，App 内不支持修改其配置。'
              : '在电脑端安装并登录浏览器插件后会自动接入，无法在 App 内开启。'
          }
        >
          {extension ? (
            <>
              <Cell
                title="状态"
                detail={<StatusDotLabel tone="ok" label="已启用" />}
              />
              <Cell title="接入时间" detail={formatTime(extension.createdAt)} />
              <Cell
                title="停用插件集成"
                titleStyle={{ flex: 1, alignItems: 'center' }}
                titleTextStyle={{ color: StatusColors.Danger }}
                onPress={() => onDeactivate(extension, '插件集成')}
              />
            </>
          ) : (
            <Cell
              title="状态"
              detail={<StatusDotLabel tone="pending" label="尚未集成" />}
            />
          )}
        </Section>

        {hosted ? (
          <>
            <Section title="云端托管集成">
              <Cell
                title="登录状态"
                detail={
                  <StatusDotLabel
                    tone={hostedLoginTone(hosted)}
                    label={hostedLoginLabel(hosted)}
                  />
                }
              />
              {!!hosted.account && (
                <Cell title="登录账号" detail={hosted.account} />
              )}
              <Cell
                title="最近登录"
                detail={formatTime(hosted.lastLoginTime)}
              />
              {!!hosted.lastLoginError && (
                <Cell title="最近错误" detail={hosted.lastLoginError} />
              )}
              <Cell title="接入时间" detail={formatTime(hosted.createdAt)} />
            </Section>

            <Section title="常用操作">
              <Cell
                title="订单同步"
                detail="查看与同步订单"
                detailIcon={'disclosure'}
                onPress={() =>
                  navigation.navigate('_erpOrderSync', {
                    providerId: connection.erpProviderId,
                    integrationId: hosted.id,
                  })
                }
              />
              <Cell
                title="员工绑定"
                detail={<StatusDotLabel {...mappingStatus(connection)} />}
                detailIcon={'disclosure'}
                onPress={() => navigation.navigate('_erpEmployeeMapping')}
              />
              <Cell
                title="同步日志"
                detail="查看历史同步记录"
                detailIcon={'disclosure'}
                onPress={() => navigation.navigate('_erpSyncLogs')}
              />
            </Section>

            <Section tail="解除后将停止订单同步，重新登录即可恢复。">
              <Cell
                title="解除云端托管集成"
                titleStyle={{ flex: 1, alignItems: 'center' }}
                titleTextStyle={{ color: StatusColors.Danger }}
                onPress={onRemoveHosted}
              />
            </Section>
          </>
        ) : (
          <Section
            title="云端托管集成"
            tail={
              canIntegrate
                ? `开启后由平台在云端登录${providerName}并同步订单，门店无需在电脑上安装浏览器插件，也不必让电脑保持常开。`
                : '当前门店未开通该功能，如需开启请联系知车客服。'
            }
          >
            {canIntegrate ? (
              <Cell
                title="尚未集成"
                detail={<StatusDotLabel tone="pending" label="去开启" />}
                detailIcon={'disclosure'}
                onPress={onSetupHosted}
              />
            ) : (
              <Cell
                title="状态"
                detail={<StatusDotLabel tone="muted" label="未开通" />}
              />
            )}
          </Section>
        )}
      </TableView>
    </ScrollView>
  );
});

/**
 * Mapping state as a dot plus a count. The count is the fact; `done` is the
 * store's declaration, and only it decides the tone — a store with members
 * it never intends to map is finished well short of everyone.
 */
const mappingStatus = (connection: ErpConnectionInfo) => {
  const { done, mapped, total } = connection.employeeMapping;
  return {
    tone: (done ? 'ok' : 'pending') as StatusTone,
    label: `${done ? '已完成' : '待完成'} ${mapped}/${total}`,
  };
};

const hostedLoginLabel = (hosted: ErpIntegrationInfo) =>
  hosted.loginStatus === ErpLoginStatus.Online
    ? '正常'
    : hosted.loginStatus === ErpLoginStatus.Expired
    ? '登录已失效'
    : hosted.loginStatus === ErpLoginStatus.Error
    ? '登录失败'
    : '未知';

const hostedLoginTone = (hosted: ErpIntegrationInfo): StatusTone =>
  hosted.loginStatus === ErpLoginStatus.Online
    ? 'ok'
    : hosted.loginStatus == null
    ? 'muted'
    : 'error';

const EmptyState = memo(({ onPress }: { onPress?: () => void }) => (
  <View css="flex: 1; align-items: center; justify-content: center; padding: 0 32px;">
    <Text
      css={`
        font-family: ${FontFamily.NotoSans.Medium};
        font-size: 16px;
        color: #101828;
        text-align: center;
      `}
    >
      还未建立 ERP 集成
    </Text>
    <Text css="margin-top: 8px; font-size: 13px; color: #667085; line-height: 20px; text-align: center;">
      {onPress
        ? '集成后可自动同步门店订单与员工信息，减少重复录入。'
        : '当前门店未开通该功能，如需开启请联系知车客服。'}
    </Text>
    {!!onPress && (
      <Button
        title="立即开始集成 →"
        style={{
          marginTop: 24,
          paddingHorizontal: 28,
          backgroundColor: '#5ba0e0',
        }}
        textStyle={{ color: '#fff', fontSize: 15 }}
        onPress={onPress}
      />
    )}
  </View>
));
